import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { twMergeConfig } from "@repo/utils/twMergeConfig";
import { ComponentProps } from "react";
import { TextWrapOptionType } from "./types";

const getColorVar = (color: "primary" | "secondary" | "inverted") => {
  switch (color) {
    case "primary":
      return "var(--typography-colors-text-heading)";
    case "secondary":
      return "var(--surface-surface-accent-tertiary)";
    case "inverted":
      return "var(--typography-colors-text-inverted)";
    default:
      return "var(--typography-colors-text-heading)";
  }
};

export const title = tv(
  {
    base: "uipm:font-[var(--font-family-sans)]",
    variants: {
      fontSize: {
        h1: "uipm:heading-1",
        h2: "uipm:heading-2",
        h3: "uipm:heading-3",
        h4: "uipm:heading-4",
        h5: "uipm:heading-5",
        h6: "uipm:heading-6",
        p: "uipm:body-text",
      },
      fontColor: {
        primary: "uipm:text-[var(--typography-colors-text-heading)]",
        secondary: "uipm:text-[var(--surface-surface-accent-tertiary)]",
        inverted: "uipm:text-[var(--typography-colors-text-inverted)]",
      },
      textWrap: {
        balance: "uipm:text-balance",
        pretty: "uipm:text-pretty",
        normal: "",
      },
    },
    defaultVariants: {
      fontSize: "h3",
      fontColor: "primary",
      textWrap: "normal",
    },
  },
  {
    twMergeConfig,
  }
);

export type TitleProps = VariantProps<typeof title> & {
  content: string;
  tagText?: string;
  level: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  fontSize?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  fontColor?: "primary" | "secondary" | "inverted";
  twoToneColor?: "primary" | "secondary" | "inverted";
  textWrap?: TextWrapOptionType;
  className?: string;
} & ComponentProps<"div">;

export const Title = React.forwardRef<
  HTMLHeadingElement | HTMLParagraphElement,
  TitleProps
>(
  (
    {
      content,
      tagText,
      level = "h3",
      fontSize = "h3",
      fontColor = "primary",
      twoToneColor,
      textWrap = "normal",
      className,
      ...props
    },
    ref
  ) => {
    const Component = level === "p" ? "p" : level;

    const twoToneStyles = twoToneColor
      ? {
          color: getColorVar(twoToneColor),
        }
      : undefined;

    return (
      <>
        {twoToneColor && (
          <style>{`
            .title-two-tone::first-line {
              color: ${getColorVar(fontColor)} !important;
            }
          `}</style>
        )}
        <Component
          ref={ref as React.Ref<HTMLHeadingElement | HTMLParagraphElement>}
          {...props}
          className={cn(
            title({
              fontSize: fontSize || level,
              fontColor: twoToneColor ? undefined : fontColor,
              textWrap,
            }),
            twoToneColor && "title-two-tone",
            className
          )}
          style={{
            ...props.style,
            ...twoToneStyles,
          }}
        >
          {tagText ? (
            <>
              {/* The tagText span is `block`, so the two spans render on
                  separate lines and the space is visually invisible — but it is
                  required so the heading's textContent reads
                  "Find parking near Venue" and not "Find parking nearVenue"
                  for crawlers. tagText is trimmed because some Storyblok
                  entries already carry a hand-typed trailing space. */}
              <span className="uipm:block uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:tracking-normal">
                {tagText.trim()}
              </span>{" "}
              <span>{content}</span>
            </>
          ) : (
            content
          )}
        </Component>
      </>
    );
  }
);

Title.displayName = "Title";
